import React from "react";
import { cva, type VariantProps } from "cva";
import { cn } from "@helpers/utils";
import BaseTooltip from "../BaseTooltip";
import { TooltipProps } from "@mui/material";

const iconButtonVariants = cva(
    // Disabled buttons keep their colors, but are faded and don't react to hover
    "inline-flex items-center justify-center transition-colors duration-150 cursor-pointer border disabled:cursor-not-allowed disabled:opacity-40",
    {
        variants: {
            style: {
                light: "bg-white border-[#7C3AED] text-[#7C3AED] enabled:hover:bg-[#F9FAFB]",
                filled: "bg-[#EDE9FE] border-[#EDE9FE] text-[#7C3AED] enabled:hover:bg-[#DDD6FE]",
                dim: "bg-[#F9FAFB] border-[#F9FAFB] text-[#686868] enabled:hover:bg-[#F3F4F6]",
            },
            size: {
                tiny: "w-5 h-5 rounded",
                small: "w-8 h-8 rounded-lg",
                medium: "w-10 h-10 rounded-lg",
            },
        },
        defaultVariants: {
            style: "filled",
            size: "medium",
        },
    }
);

type IconButtonProps = Omit<React.ComponentProps<"button">, "style"> &
    VariantProps<typeof iconButtonVariants> & {
        icon: JSX.Element;
        tip?: string | JSX.Element; // The tooltip text. If not provided, no tooltip will be shown
        tooltipPlacement?: TooltipProps["placement"];
        color?: string; // For backward compatibility, passed through but not used
    };

export default function BaseMaterialIconButton({
    icon,
    size,
    style,
    onClick,
    tip,
    tooltipPlacement,
    className,
    color, // Accept but ignore, for backward compatibility
    ...props
}: IconButtonProps) {
    const button = (
        <button
            type="button"
            onClick={onClick}
            className={cn(iconButtonVariants({ style, size }), className)}
            {...props}
        >
            {icon}
        </button>
    );

    // If a tooltip is provided, wrap the button in a Tooltip component
    if (tip) {
        return (
            <BaseTooltip title={tip} placement={tooltipPlacement} className="flex items-center">
                {button}
            </BaseTooltip>
        );
    }

    return button;
}
